import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { makeStyles } from "tss-react/mui";

import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import Autocomplete from "@mui/material/Autocomplete";
import Grid from "@mui/material/Grid";
import Checkbox from "@mui/material/Checkbox";

import { showToast, setUser } from "../../store/actions";
import WorkspaceClass from "../../service/workspace";
import { ButtonUI, KMKDialog } from "../Common";
import { regexName } from "../../util/regularExpress";
import { checkReservedKeywords } from "../../util/restrictedStatement";

const useStyles = makeStyles()((theme) => ({
  dialogPaper: {
    "& .MuiPaper-root": {
      maxWidth: "600px",
    },
    "& .MuiFormHelperText-root": {
      marginLeft: 0,
    },
    "& .MuiGrid-item": {
      paddingTop: "15px",
    },
    "& .MuiGrid-item .MuiTextField-root": {
      marginTop: 0,
    },
    "& .MuiDivider-root": {
      marginTop: "5px",
      marginBottom: "9px",
    },
    "& .MuiFormControl-root": {
      margin: 0,
    },
    "& .MuiDialogActions-root": {
      position: "absolute",
      bottom: "10px",
      right: "20px",
    },
  },
  formContainer: {
    display: "grid",
    width: "100%",
    textAlign: "left",
  },
  container: {
    padding: "24px 24px 0 24px",
    backgroundColor: "#FFFFFF",
  },
  Toolbar: {
    fontSize: "1.1rem",
    backgroundColor: "white",
    color: "black",
  },
  ActionBox: { display: "flex", justifyContent: "end", alignContent: "center", padding: "12px 24px 16px 24px" },
}));

export default function AddForm({ open, setOpen, loadWorkspaceList }) {
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState("");
  const [database, setDatabase] = useState("");
  const [dbError, setDBError] = useState("");
  const [type, setType] = useState(1);
  const [owner, setOwner] = useState({});
  const [totalUserList, setTotalUserList] = useState([]);
  const [userList, setUserList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dataVaultFlag, setDataVaultFlag] = useState(true);

  const { classes } = useStyles();
  const dispatch = useDispatch();

  const user = useSelector((state) => state.user);
  const { showEnforceDataVault } = useSelector((state) => state.config);

  //Name
  const handleName = (str, setData) => {
    const isValid = regexName(str, "alphabetStart");
    if (isValid) setData(str);

    if (!str || str.length >= 2) setNameError("");
  };

  //Db name
  const handleDBName = (str, setData) => {
    const isValid = regexName(str, "alphaWithUnderscore");

    if (isValid) {
      setData(str);

      if (str.length && str.length < 2) setDBError("Database name must be 2-50 characters long");
      else if (checkReservedKeywords(str)) setDBError("Reserved keyword.");
      else setDBError("");
    }
  };

  const onClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    const loadUserList = async () => {
      setLoading(true);

      const workspace = new WorkspaceClass();
      const result = await workspace.getUserList();

      if (result?.users) {
        let userArr = [];
        if (result.users.length)
          result.users.forEach((user) => {
            userArr.push({
              value: user.User_id,
              text: `${user.FirstName} ${user.LastName} (${user.UserName})`,
              Workspace_id: user.Workspace_id,
            });
          });

        setUserList(userArr);
        setTotalUserList(userArr);
        setOwner(userArr.find((v) => v.value === user.currentUser.User_id) || {});
      }

      setLoading(false);
    };
    loadUserList();
  }, [user.currentUser.User_id]);

  const updateType = (value) => {
    setType(value);

    let userArr = [...totalUserList];
    if (userArr.length && value === 2) userArr = userArr.filter((user) => user.Workspace_id === null);
    setUserList(userArr);
    setOwner(userArr.find((v) => v.value === user.currentUser.User_id) || {});
  };

  const onSave = async () => {
    setLoading(true);
    const workspace = new WorkspaceClass();
    const result = await workspace.createWorkspace({
      name,
      database,
      type,
      ownerId: owner.value,
      dataVaultFlag: dataVaultFlag ? 1 : 0,
    });

    if (!user.currentUser.connections)
      dispatch(
        setUser({
          ...user.currentUser,
          connections: true,
        })
      );

    if (result.success) {
      loadWorkspaceList();
      dispatch(
        showToast({
          message: result.message,
          type: "success",
        })
      );
      setOpen(false);
    }
    setLoading(false);
  };

  return (
    <KMKDialog className={classes.dialogPaper} open={open} data-id="addWS">
      <AppBar sx={{ position: "relative" }}>
        <Toolbar className={classes.Toolbar}>{"Add Workspace"} </Toolbar>
        <IconButton
          aria-label="close"
          onClick={() => onClose()}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "black",
          }}
        >
          <CloseIcon />
        </IconButton>
      </AppBar>
      <Box sx={{ width: "100%" }} className={classes.container}>
        <Backdrop sx={{ backgroundColor: "transparent", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
          <CircularProgress color="primary" />
        </Backdrop>
        <Box sx={{ width: "100%" }} component="form" className={classes.formContainer}>
          <Grid container spacing={2}>
            <Grid size={12}>
              <TextField
                data-id="addWSName"
                required
                autoFocus
                value={name}
                inputProps={{ maxLength: 50 }}
                onChange={(e) => handleName(e.target.value, setName)}
                onBlur={(e) => {
                  const newVal = e.target.value.replaceAll(" ", "_").replace(/[\W-]/g, "").toUpperCase();
                  handleDBName(newVal, setDatabase);

                  if (e.target.value && e.target.value.length < 2)
                    setNameError("Workspace name must be 2-50 characters long");
                }}
                fullWidth
                error={!!nameError}
                helperText={nameError}
                label="Workspace Name"
                variant="outlined"
                sx={{ my: 2 }}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                data-id="addDBName"
                required
                value={database}
                inputProps={{ maxLength: 50 }}
                onChange={(e) => handleDBName(e.target.value, setDatabase)}
                fullWidth
                error={!!dbError}
                helperText={dbError}
                label="Database Name"
                variant="outlined"
                sx={{ my: 2 }}
              />
            </Grid>
            <Grid size={5}>
              <FormControl required fullWidth variant="outlined" sx={{ my: 2 }}>
                <FormLabel>Type</FormLabel>
                <RadioGroup
                  row
                  name="type"
                  value={type}
                  onChange={(e) => updateType(e.target.value)}
                  data-id="addWSType"
                  data-wstype={type}
                >
                  <FormControlLabel value={1} control={<Radio />} label="Project" />
                  <FormControlLabel value={2} control={<Radio />} label="Personal" />
                </RadioGroup>
              </FormControl>
            </Grid>

            {showEnforceDataVault && (
              <Grid size={7}>
                <FormControl fullWidth variant="outlined" sx={{ my: 2 }}>
                  <FormLabel>&nbsp;</FormLabel>
                  <FormControlLabel
                    data-id="addWSDV2"
                    data-dv2-enabled={dataVaultFlag}
                    control={<Checkbox checked={dataVaultFlag} />}
                    label="Enforce Data Vault 2.0 Compliance"
                    onChange={(e) => setDataVaultFlag(e.target.checked)}
                  />
                </FormControl>
              </Grid>
            )}
            <Grid size={12}>
              <Autocomplete
                data-id="addWSOwner"
                fullWidth
                options={userList}
                getOptionLabel={(option) => {
                  return option.text || "";
                }}
                value={owner}
                renderOption={(props, option) => (
                  <li {...props} key={option.value}>
                    {option.text}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField required variant="outlined" {...params} label="Owner" placeholder="Owner" />
                )}
                onChange={(e, selectedValues) => setOwner(selectedValues)}
                ListboxProps={{
                  style: {
                    maxHeight: "185px",
                  },
                }}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>
      <Box className={classes.ActionBox}>
        <ButtonUI variant="contained" color="secondary" onClick={onClose} data-id="addWSCancel">
          Cancel
        </ButtonUI>
        <ButtonUI
          data-id="addWSSave"
          disabled={!(name.trim() && database.trim() && type && owner && owner.value && !dbError) || loading}
          variant="contained"
          color="success"
          onClick={onSave}
          style={{ marginLeft: 15 }}
        >
          Create Workspace
        </ButtonUI>
      </Box>
    </KMKDialog>
  );
}
